import { useEffect, useRef } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, drawSelection, highlightActiveLine, dropCursor, rectangularSelection, crosshairCursor, placeholder } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { bracketMatching, indentOnInput } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import { searchKeymap, highlightSelectionMatches, search } from '@codemirror/search';
import { cortexTheme, cortexHighlight } from './theme';
import { wikilinkCompletion, wikilinkDecorations, frontmatterDecorations } from './wikilink';
import { attachmentPaste } from './paste';

export interface EditorProps {
  /** Identity of the document; when it changes the editor is reset. */
  docKey: string;
  value: string;
  /** Bumped when `value` changed outside the editor (reload, properties panel). */
  externalVersion: number;
  onChange: (raw: string) => void;
  onSave: () => void;
  onOpenLink: (target: string, opts: { split?: boolean }) => void;
  notePath?: string;
  notify: (msg: string, kind?: 'info' | 'warn' | 'crit') => void;
  autoFocus?: boolean;
}

export function Editor({ docKey, value, externalVersion, onChange, onSave, onOpenLink, notePath, notify, autoFocus }: EditorProps) {
  const host = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const cb = useRef({ onChange, onSave, onOpenLink, notePath, notify });
  cb.current = { onChange, onSave, onOpenLink, notePath, notify };
  const lastExternal = useRef(externalVersion);

  useEffect(() => {
    if (!host.current) return;
    const readOnly = new Compartment();
    const state = EditorState.create({
      doc: value,
      extensions: [
        cortexTheme,
        cortexHighlight,
        markdown({ base: markdownLanguage, codeLanguages: languages, addKeymap: true }),
        history(),
        drawSelection(),
        dropCursor(),
        highlightActiveLine(),
        bracketMatching(),
        indentOnInput(),
        closeBrackets(),
        rectangularSelection(),
        crosshairCursor(),
        highlightSelectionMatches(),
        search({ top: true }),
        placeholder('Start writing… [[ links notes, # tags, ⌘K for everything else'),
        wikilinkCompletion(),
        wikilinkDecorations((t, o) => cb.current.onOpenLink(t, o)),
        frontmatterDecorations,
        attachmentPaste(() => cb.current.notePath, (m, k) => cb.current.notify(m, k)),
        EditorView.lineWrapping,
        readOnly.of(EditorState.readOnly.of(false)),
        keymap.of([
          { key: 'Mod-s', run: () => (cb.current.onSave(), true) },
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          ...completionKeymap,
          indentWithTab,
        ]),
        EditorView.updateListener.of((u) => {
          if (u.docChanged) cb.current.onChange(u.state.doc.toString());
        }),
      ],
    });
    const view = new EditorView({ state, parent: host.current });
    viewRef.current = view;
    if (autoFocus) view.focus();
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // docKey identifies the document; value at mount time only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey]);

  // External replacement (reload from disk, properties panel edit).
  useEffect(() => {
    const view = viewRef.current;
    if (!view || externalVersion === lastExternal.current) return;
    lastExternal.current = externalVersion;
    const cur = view.state.doc.toString();
    if (cur === value) return;
    const sel = Math.min(view.state.selection.main.head, value.length);
    view.dispatch({ changes: { from: 0, to: cur.length, insert: value }, selection: { anchor: sel } });
  }, [externalVersion, value]);

  // Jump to heading (from [[Note#Heading]] links).
  useEffect(() => {
    const onGoto = (e: Event) => {
      const { noteId, heading } = (e as CustomEvent).detail as { noteId: string; heading: string };
      if (!docKey.startsWith(noteId)) return;
      const view = viewRef.current;
      if (!view) return;
      const text = view.state.doc.toString();
      const re = new RegExp(`^#{1,6}\\s+${heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'mi');
      const m = re.exec(text);
      if (!m) return;
      view.dispatch({ selection: { anchor: m.index }, effects: EditorView.scrollIntoView(m.index, { y: 'start', yMargin: 24 }) });
      view.focus();
    };
    document.addEventListener('cortex:goto-heading', onGoto);
    return () => document.removeEventListener('cortex:goto-heading', onGoto);
  }, [docKey]);

  return <div className="editor-host" ref={host} />;
}
